import { ArrowPointLeft, ArrowPointRight } from "assets/icons/Common";
import { IconButton, Row } from "components/primitives";
import { CalendarItemData } from "components/widgets/Calendar/interfaces";
import { FC } from "react";
import styled, { css } from "styled-components";
import { getDateString } from "util/getDateString";

interface Props {
  selectedDate: Date;
  month: number;
  year: number;
  dateSelected: (date: Date) => void;
  monthChanged: (month: number, year: number) => void;
  startOfWeek: "Monday" | "Sunday";
  colourPrevDays: boolean;
  highlightDates?: Date[];
  assignedEvents?: { [date: string]: CalendarItemData[] };
}

export const Calendar: FC<Props> = ({
  selectedDate,
  month,
  year,
  dateSelected,
  monthChanged,
  startOfWeek,
  colourPrevDays,
  highlightDates,
  assignedEvents,
}) => {
  const DAYS_OF_THE_WEEK_DATA = {
    Monday: {
      days: ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"],
      startDayAdjustment: 2,
    },
    Sunday: {
      days: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
      startDayAdjustment: 1,
    },
  };
  const MONTHS = [
    "JANUARY",
    "FEBRUARY",
    "MARCH",
    "APRIL",
    "MAY",
    "JUNE",
    "JULY",
    "AUGUST",
    "SEPTEMBER",
    "OCTOBER",
    "NOVEMBER",
    "DECEMBER",
  ];

  const today = new Date();
  // Determine if we need 5 or 6 rows to display the month correctly
  const clendarLength =
    daysInMonth() +
      (getStartDayOfMonth() + 1) -
      DAYS_OF_THE_WEEK_DATA[startOfWeek].startDayAdjustment >
    35
      ? 42
      : 35;

  function getStartDayOfMonth() {
    const startDate = new Date(year, month, 1).getDay();
    return startDate === 0 ? 7 : startDate;
  }

  function daysInMonth() {
    return new Date(year, month + 1, 0).getDate();
  }

  function changeMonth(direction: number) {
    const newMonth = new Date(year, month + direction);
    monthChanged(newMonth.getMonth(), newMonth.getFullYear());
  }

  function getDateFromIndex(index: number) {
    const date = new Date(year, month, 0);
    date.setDate(date.getDate() + index);
    return date;
  }

  function isDayHighlighted(date: Date) {
    if (highlightDates) {
      for (let highlightDate of highlightDates) {
        if (
          highlightDate.getDate() === date.getDate() &&
          highlightDate.getMonth() === date.getMonth() &&
          highlightDate.getFullYear() === date.getFullYear()
        ) {
          return true;
        }
      }
    }
    return false;
  }

  function isEventDay(date: Date) {
    const dateString = getDateString(date);
    if (assignedEvents) {
      if (dateString in assignedEvents) {
        return true;
      }
    }
    return false;
  }

  return (
    <Frame>
      <Header>
        <ChangeMonthButton
          withShadow={false}
          Icon={ArrowPointLeft}
          accentColor="var(--secondary)"
          onClick={() => changeMonth(-1)}
        />
        <p>
          {MONTHS[month]} {year}
        </p>
        <ChangeMonthButton
          withShadow={false}
          Icon={ArrowPointRight}
          accentColor="var(--secondary)"
          onClick={() => changeMonth(1)}
        />
      </Header>
      <Body>
        {DAYS_OF_THE_WEEK_DATA[startOfWeek].days.map((d) => (
          <DayHeader key={d}>
            <strong>{d}</strong>
          </DayHeader>
        ))}
        {Array(clendarLength)
          .fill(null)
          .map((_, index) => {
            const d =
              index - getStartDayOfMonth() + DAYS_OF_THE_WEEK_DATA[startOfWeek].startDayAdjustment;
            const date = getDateFromIndex(d);
            return (
              <Day
                key={index}
                selectable={true}
                isToday={
                  d === today.getDate() &&
                  month === today.getMonth() &&
                  year === selectedDate.getFullYear()
                }
                isSelected={
                  d === selectedDate.getDate() &&
                  month === selectedDate.getMonth() &&
                  year === selectedDate.getFullYear()
                }
                onClick={() => dateSelected(new Date(year, month, d))}
                isThisMonth={month === date.getMonth()}
                isPrevDate={date < today && colourPrevDays}
                isActive={isEventDay(date)}
              >
                <div className="date">{date.getDate()}</div>
                {isDayHighlighted(date) && (
                  <DayHighlight isToday={d === today.getDate() && month === today.getMonth()} />
                )}
                {isEventDay(date) && assignedEvents && (
                  <EmojiRow>
                    {assignedEvents[getDateString(date)].map((event, index) => (
                      <EventWrap key={index}>
                        {(event?.event ?? "").trim() !== "" && <EventName>{event.event}</EventName>}
                        {event.image}
                      </EventWrap>
                    ))}
                  </EmojiRow>
                )}
              </Day>
            );
          })}
      </Body>
    </Frame>
  );
};

const Frame = styled.div`
  height: 100%;
`;

const Header = styled(Row)`
  font-size: 5cqw;
  font-weight: bold;
  padding: 2cqw 2cqw 1cqw 2cqw;
  justify-content: space-between;
  height: 2.5em;
  color: var(--widget-primary);

  p {
    margin: auto;
  }
`;

const Body = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  padding: 1px;
`;

const DayHeader = styled.div`
  width: 14.285%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: white;
  color: var(--widget-primary);
  font-size: 4cqw;
  aspect-ratio: 1;
`;

const Day = styled.div<{
  isToday?: boolean;
  isSelected?: boolean;
  isThisMonth?: boolean;
  selectable?: boolean;
  isPrevDate?: boolean;
  isActive?: boolean;
}>`
  width: 14.285%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: white;
  outline: 1px solid black;
  font-weight: 600;
  font-size: 4cqw;
  position: relative;
  aspect-ratio: 1;

  ${(props) =>
    props.selectable &&
    css`
      cursor: pointer;
    `}
  ${(props) =>
    props.isToday &&
    css`
      background-color: var(--primary);
      color: white;
    `}
  ${(props) =>
    !props.isThisMonth &&
    props.selectable &&
    css`
      color: #adadad;
      background-color: #efefef;
    `};
  ${(props) =>
    props.isPrevDate &&
    !props.isToday &&
    props.isThisMonth &&
    css`
      background-color: #eaf0ff;
    `};
  .date {
    width: 70%;
    height: 70%;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;

    ${(props) =>
      props.isSelected &&
      !props.isToday &&
      css`
        background-color: #4a47e0;
        color: white;
      `}

    ${(props) =>
      props.isActive &&
      css`
        width: 45%;
        height: 45%;
        position: absolute;
        top: 1cqw;
        left: 1cqw;
        font-size: 3cqw;
      `}
  }
`;

const DayHighlight = styled.span<{ isToday: boolean }>`
  position: absolute;
  top: 5px;
  right: 5px;
  width: 8px;
  height: 8px;
  background-color: ${(props) => (props.isToday ? "white" : "var(--primary)")};
  border-radius: 50%;
`;

const ChangeMonthButton = styled(IconButton)`
  height: 100%;
  aspect-ratio: 1;
`;

const EmojiRow = styled(Row)`
  position: absolute;
  bottom: 3px;
  padding-bottom: 3px;
`;
const EventWrap = styled.div`
  font-size: 3cqw;
  line-height: 1.2;
  position: relative;
`;

const EventName = styled.div`
  display: none;
  position: absolute;
  top: calc(-1 * (100% + 6cqw));
  left: 50%;
  transform: translateX(-50%);
  box-shadow: 0.5cqw 1cqw 1cqw rgba(0, 0, 0, 0.4);
  white-space: nowrap;
  background: #fff;
  color: #111;
  padding: 1.5cqw;
  z-index: 1;
  border-radius: 1cqw;
  font-size: 4cqw;
  ${EventWrap}:hover & {
    display: block;
  }
`;
